/* eslint-disable object-curly-spacing */
/* eslint-disable no-undef */
/* eslint-disable own-handles */
/* eslint-disable no-innerhtml-assign */
/* eslint-disable consistent-method-names */
/* eslint-disable no-jsdoc */
/**
 * Tile layer for Leaflet that use the
 * <a href="https://developer.tomtom.com/online-maps/online-maps-documentation/vector">vector online maps</a>
 * from TomTom.
 *
 * The current vector renderer implementation is based on the
 * [Mapbox GL JS library](https://www.mapbox.com/mapbox-gl-js/api/).
 *
 * The recommended way to use vector maps is by using the "source" option of {{#crossLink "L.Map"}}{{/crossLink}} which
 * will create an instance of this class and immediately attach it to the newly created map instance.
 *
 * @example
 * ```js
 * // Vector layer instance with factory vector styles
 * var vectorLayer = new L.TomTomVectorLayer({
 *   vector: {
 *     key: "<Your API key>"
 *   }
 * });
 * ```
 *
 * @class TomTomVectorLayer
 * @namespace L
 * @module Map
 * @extends L.Class
 * @constructor
 * @param {Object} [options] It accepts all the options that
 *     <a href="http://leafletjs.com/reference-${leaflet.version}.html#tilelayer-options">Leaflet's TileLayer</a>
 *     allows.
 *     The additional options, which are TomTom specific, are described below.
 * @param {"main"|"night"} [options.style="main"] The base map style to use
 * @param {"basic"|"hybrid"|"labels"} [options.layer="basic"] The base map's layer to use
 * @param {Object} [options.vector] Options specific for vector layer
 * @param {String} [options.vector.key] API key that can be obtained from
 *     <a href="http://developer.tomtom.com">TomTom's developer portal</a>.
 *     When the key is passed on the map's initialization, it is set globally for all the services.
 *     It can be overwritten with the `tomtom.key()` method globally or per service
 *     with `tomtom.<service_name>.key()` functions.
 *     It is also possible to pass a specific key while working with instances of the services.
 * @param {Object} [options.vector.renderer] Mapbox GL JS Map options as described in the
 *     <a href="https://www.mapbox.com/mapbox-gl-js/api/#Map">Mapbox API Docs</a>
 * @param {"error"|"raster"} [options.vector.fallback="error"] Defines the action when the browser doesn't have
 *     support for WebGL (needed by the vector layer).
 *     The possible values are:
 *  - `"error"` will throw a javascript Error informing about the problem.
 *  - `"raster"` will automatically switch to the raster layer.
 */
/* jshint nomen:false */
define([
    'leaflet',
    'security',
    'mapbox-gl-leaflet',
    'display/layers/mapTheme',
    'apiKeyNames',
    '../../utils',
    'services/utils',
    'requester/analytics'
], function(L, security, mapboxGLLeaflet, mapTheme, ApiKeyNames, Utils, ServiceUtils, analytics) {
    'use strict';

    var EVENTS = {
        /**
         * Fired immediately after the style was fully loaded.
         * @event STYLE_LOAD_END
         * @type String
         */
        STYLE_LOAD_END: 'tomtom.styleloadend',

        /**
         * Fired when an error occurs during the initialization of the vector layer.
         * @event MAP_ERROR
         * @type String
         */
        MAP_ERROR: 'tomtom.error'
    };

    function mapboxStyleLoadedEventHandler(e) {
        if (e.dataType !== 'style') {
            return;
        }
        this.fire(L.TomTomVectorLayer.Events.STYLE_LOAD_END);
    }

    function mapErrorHandler(event) {
        this.fire(L.TomTomVectorLayer.Events.MAP_ERROR, event);
    }

    function toFront(el) {
        var parent = el.parentNode;
        if (parent.lastChild !== el) {
            parent.appendChild(el);
        }
    }

    function toBack(el) {
        var parent = el.parentNode;
        if (parent.firstChild !== el) {
            parent.insertBefore(el, parent.firstChild);
        }
    }

    L.TomTomVectorLayer = mapboxGLLeaflet.extend({

        Events: EVENTS,

        statics: {
            Events: EVENTS
        },

        /**
         * Name of vector layer. It can be used to differentiate layers.
         * @property name
         * @default "vector"
         * @type String
         */
        name: 'vector',

        onAdd: function(map) {
            mapboxGLLeaflet.prototype.onAdd.call(this, map);
            this.bringToBack();
            this._glMap.on('data', this._mapboxStyleLoadedEventHandler);
            this._glMap.on('error', this._mapErrorHandler);
            Utils.updateMaxZoom(map, this.name);
        },

        onRemove: function(map) {
            this._glMap.off('data', this._mapboxStyleLoadedEventHandler);
            this._glMap.off('error', this._mapErrorHandler);
            mapboxGLLeaflet.prototype.onRemove.call(this, map);
        },

        _updateZIndex: function() {
            if (this._glContainer && this.options.zIndex !== undefined && this.options.zIndex !== null) {
                this._glContainer.style.zIndex = this.options.zIndex;
            }
        },

        _setAutoZIndex: function(compare) {
            // go through all other layers of the same pane, set zIndex to max + 1 (front) or min - 1 (back)
            var i, len, zIndex,
                layers = this.getPane().children,
                edgeZIndex = -compare(-Infinity, Infinity); // -Infinity for max, Infinity for min
            for (i = 0, len = layers.length, zIndex; i < len; i++) {
                zIndex = layers[i].style.zIndex;
                if (layers[i] !== this._glContainer && zIndex) {
                    edgeZIndex = compare(edgeZIndex, Number(zIndex));
                }
            }
            if (isFinite(edgeZIndex)) {
                this.options.zIndex = edgeZIndex + compare(-1, 1);
                this._updateZIndex();
            }
        },

        bringToBack: function() {
            if (this._map && this._glContainer) {
                toBack(this._glContainer);
                this._setAutoZIndex(Math.min);
            }
            return this;
        },

        bringToFront: function() {
            if (this._map && this._glContainer) {
                toFront(this._glContainer);
                this._setAutoZIndex(Math.max);
            }
            return this;
        },

        _validateUrl: function(url) {
            var pattern = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;

            return Boolean(ServiceUtils.isString(url) && pattern.test(url));
        },

        _getResourcesUrls: function(options) {
            return {
                glyphs: this._validateUrl(options.glyphsUrl) ? options.glyphsUrl :
                    options.basePath + '/glyphs/{fontstack}/{range}.pbf',
                sprite: this._validateUrl(options.spriteUrl) ? options.spriteUrl : options.basePath + '/sprites/sprite'
            };
        },
        /**
         * Returns the vector style object currently applied to the vector layer.
         *
         * @method getStyle
         * @return {Object} A object with the current style or undefined if no style was applied yet.
         */
        getStyle: function() {
            return this._glMap.style.stylesheet;
        },
        /**
         * Sets the style for the vector layer.
         *
         * It expects an object formatted in compliance with the
         * [Mapbox style specification](https://www.mapbox.com/mapbox-gl-js/style-spec/).
         *
         * @method setStyle
         * @param {Object} style object which will be applied to vectorLayer
         */
        setStyle: function(style) {
            if (this._glMap) {
                this._glMap.setStyle(style);
            }
        },

        updateOptions: function(options) {
            var theme = mapTheme.getTheme([this.options, options], this.name);
            this.options.layer = theme.layer;
            this.options.style = theme.style;
            this.options.renderer.style.theme = theme;
            if (this._glMap) {
                this._glMap.setStyle(this.options.renderer.style);
            }
        },

        _initializeDefaultStyleOptions: function(options) {
            var defaultStyleUrls;
            options.basePath = options.basePath !== undefined ? options.basePath : '/sdk';
            defaultStyleUrls = {
                main: {
                    basic: options.basePath + '/styles/basic_main.json',
                    hybrid: options.basePath + '/styles/hybrid_main.json',
                    labels: options.basePath + '/styles/labels_main.json'
                },
                night: {
                    basic: options.basePath + '/styles/basic_night.json',
                    hybrid: options.basePath + '/styles/hybrid_night.json',
                    labels: options.basePath + '/styles/labels_night.json'
                }
            };

            options.styleUrlMapping = L.Util.extend(defaultStyleUrls, options.styleUrlMapping);
            return options;
        },

        initialize: function(options) {
            var theme, vectorOptions, rendererOptions, resourceUrls;
            options = L.extend({}, options);
            this._initializeDefaultStyleOptions(options);
            theme = mapTheme.getTheme([options, options.vector], this.name);
            vectorOptions = L.extend({}, options.vector);
            vectorOptions.renderer = vectorOptions.renderer || {};
            security.setKey(ApiKeyNames.vector, vectorOptions.key);
            resourceUrls = this._getResourcesUrls(options);
            rendererOptions = {
                accessToken: 'no-token',
                style: {
                    resourceUrls: resourceUrls,
                    styleUrlMapping: options.styleUrlMapping,
                    theme: theme,
                    language: options.language,
                    geopolView: options.geopolView,
                    securityKeyName: options.securityKeyName || ApiKeyNames.vector
                },
                transformRequest: function(url) {
                    var headers = {};
                    headers[analytics.getHeaderName()] = analytics.getHeaderContent();
                    return {
                        url: url,
                        headers: headers
                    };
                }
            };
            vectorOptions.renderer = L.extend(rendererOptions, vectorOptions.renderer);
            vectorOptions.minZoom = 2;
            vectorOptions.maxZoom = 22;
            vectorOptions.style = theme.style;
            vectorOptions.layer = theme.layer;
            L.setOptions(this, vectorOptions);
            this._mapboxStyleLoadedEventHandler = mapboxStyleLoadedEventHandler.bind(this);
            this._mapErrorHandler = mapErrorHandler.bind(this);
            mapboxGLLeaflet.prototype.initialize.call(this, vectorOptions);
        }
    });

    ServiceUtils.addDeprecatedProperty(L.TomTomVectorLayer, 'EVENTS', 'Events');

    L.tomTomVectorLayer = function(options) {
        return new L.TomTomVectorLayer(options);
    };

    return L.TomTomVectorLayer;
});




// WEBPACK FOOTER //
// ./display/layers/vector/vectorLayer.js